import React, { ElementType } from 'react';
import {
	GetMemberProgramsV2Query,
	ProgramContentItemCategory,
	ProgramKind,
} from '@src/graphqlTypes/catalogServiceTypes';
import {
	formatStartDate,
	getCompletionTime,
	getEffort,
	getInterests,
	getMemberPlanFundingLevel,
} from '@src/utilities/programAttributeUtility';
import CheckIcon from '@guildeducationinc/recess/icons/CheckIcon';
import CloseIcon from '@guildeducationinc/recess/icons/CloseIcon';
import { Colors, TextLink, Typography } from '@guildeducationinc/recess';
import { Link } from 'react-router-dom';
import { generateProgramDetailsRequirementLink } from '@src/utilities/urlUtility';
import { formatMoney } from '@src/utilities/numberFormattingUtility';
import { ProgramFormat } from './programFormat';

export enum ProgramAttributes {
	'Location' = 'Location',
	'Type' = 'Type',
	'Effort' = 'Effort',
	'Funding' = 'Funding',
	'Program start' = 'Program start',
	'Length' = 'Length',
	'Guild Coaching' = 'Guild Coaching',
	'Interest' = 'Interest',
	'Location requirements' = 'Location requirements',
	'Prerequisites' = 'Prerequisites',
	'Skill requirements' = 'Skill requirements',
	'Other requirements' = 'Other requirements',
	'Work experience requirements' = 'Work experience requirements',
	'Technology requirements' = 'Technology requirements',
	'GPA requirements' = 'GPA requirements',
}

export const highlightedAttributes = [
	ProgramAttributes.Location,
	ProgramAttributes.Type,
	ProgramAttributes.Effort,
	ProgramAttributes.Funding,
	ProgramAttributes['Program start'],
	ProgramAttributes.Length,
	ProgramAttributes['Guild Coaching'],
];

export const getProgramFundingMetadata = (fundingLevel: number) => {
	switch (fundingLevel) {
		case -1:
			return 'Your employer will pay 100% of your tuition costs directly to your school. If applicable, grants and scholarships are applied to tuition and fees before employer funding.';
		default:
			return `Your employer will pay up to ${formatMoney(
				fundingLevel
			)} of your tuition costs directly to your school each year.`;
	}
};

export const getDppFundingMetadata = (fundingLevel: number) => {
	switch (fundingLevel) {
		case -1:
			return "You'll apply for a Sponsorship Letter that tells your school to expect 100% tuition payment from your employer.";
		default:
			return `You'll apply for a Sponsorship Letter that tells your school to expect payment from your employer, up to ${formatMoney(
				fundingLevel
			)} each year.`;
	}
};

export const getProgramTypeMetadata = (programType: string) => {
	switch (programType) {
		case 'ASSOCIATES':
			return {
				body: 'Associate degree',
				popoverCopy:
					'An associate degree provides entry-level training in a specific career field.',
			};
		case 'BACHELORS':
			return {
				body: "Bachelor's degree",
				popoverCopy:
					"A bachelor's degree provides broad learning in a range of subjects, and focused study in one area.",
			};
		case 'COLLEGE_PREP':
			return {
				body: 'College Start',
				popoverCopy:
					'College Start lets you prepare for entering a degree program while earning real college credit.',
			};
		case 'DOCTORAL':
			return {
				body: 'Doctoral degree',
				popoverCopy:
					"A doctoral degree provides highly advanced study in a specialized subject, and requires a bachelor's or master's degree.",
			};
		case 'HIGH_SCHOOL':
			return {
				body: 'High School Diploma or Equivalency',
				popoverCopy:
					'High school diploma or equivalency prepares you for future work or study, and helps you access work or education opportunities.',
			};
		case 'LANGUAGE':
			return {
				body: 'Language',
				popoverCopy:
					'Language programs help you develop skills in using a language for everyday life or for specific purposes.',
			};
		case 'MASTERS':
			return {
				body: "Master's degree",
				popoverCopy:
					"A master's degree provides advanced study in a specific subject or professional area.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};

export const getCertificateMetadata = (certificateType: string) => {
	switch (certificateType) {
		case 'INTRODUCTORY_CERTIFICATE':
			return {
				body: 'Introductory certificate',
				popoverCopy:
					'An introductory certificate or bootcamp is a short education or training program that requires no prior experience.',
			};
		case 'INTERMEDIATE_CERTIFICATE':
			return {
				body: 'Intermediate certificate',
				popoverCopy:
					"An intermediate certificate or bootcamp is a short program that provides further training on skills you've begun to develop.",
			};
		case 'ADVANCED_CERTIFICATE':
			return {
				body: 'Advanced certificate',
				popoverCopy:
					"An advanced certificate or bootcamp is a short program that provides training to make you even better at skills you've already built.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};

export const getProgramFormatMetadata = (programFormat: string) => {
	switch (programFormat) {
		case 'Online Program':
			return {
				body: 'Online',
				popoverCopy: 'The program is delivered 100% on the internet.',
			};
		case 'online_or_in_person':
			return {
				body: 'Online or in-person',
				popoverCopy:
					'The program is delivered either on the internet or onsite at learning provider or other site.',
			};
		case 'online_and_in_person':
			return {
				body: 'Hybrid online & in-person',
				popoverCopy:
					'The program is delivered both on the internet and onsite at learning provider or other site.',
			};
		case 'in_person':
			return {
				body: 'In-person',
				popoverCopy:
					"The program is delivered at your school's location and not over the internet.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};

type ProgramAttributeTextLinkConfig = {
	introCopy?: string;
	linkCopy: string;
	onClick: (linkText: string, programId: string) => void;
	outroCopy?: string;
};

const renderCellWithTextLink = (
	configOptions: ProgramAttributeTextLinkConfig,
	program: GetMemberProgramsV2Query['memberPrograms'][0]
) => {
	const { introCopy, linkCopy, onClick, outroCopy } = configOptions;
	return (
		<Typography>
			{introCopy}{' '}
			<TextLink
				component={Link as unknown as ElementType}
				onClick={() => onClick(linkCopy, program.id)}
				to={generateProgramDetailsRequirementLink(program)}
				variant='standalone'
			>
				{linkCopy}
			</TextLink>{' '}
			{outroCopy}
		</Typography>
	);
};

export const getAttributeData = (
	attribute: ProgramAttributes,
	program: GetMemberProgramsV2Query['memberPrograms'][0],
	onClick?: (linkText: string, programId: string) => void
): string | JSX.Element => {
	switch (attribute) {
		case ProgramAttributes.Location:
			return getProgramFormatMetadata(program.programFormat).body || 'Location unavailable';
		case ProgramAttributes.Type:
			if (!program?.programKind || !program?.shortProgramKind) {
				return 'Type unavailable';
			}
			return program?.programKind?.includes(ProgramKind.CERTIFICATE)
				? getCertificateMetadata(program?.programKind).body
				: getProgramTypeMetadata(program?.shortProgramKind).body;
		case ProgramAttributes.Effort:
			return getEffort(program) || 'Effort unavailable';
		case ProgramAttributes.Funding:
			return getMemberPlanFundingLevel(program.fundingLevel);
		case ProgramAttributes['Program start']:
			if (program.planProgramStatus === 'ep-capped') {
				return 'Program is full';
			}
			return formatStartDate(program);
		case ProgramAttributes.Length:
			return getCompletionTime(program);
		case ProgramAttributes['Guild Coaching']:
			return program.studentFacingAcademicPartner.coachingIncluded ? (
				<div className='flex flexContent--center'>
					<CheckIcon className='margin--right_4' color={Colors.Success} size={24} />
					Yes
				</div>
			) : (
				<div className='flex flexContent--center'>
					<CloseIcon className='margin--right_4' color={Colors.Danger} size={24} />
					No
				</div>
			);
		case ProgramAttributes.Interest:
			return getInterests(program);
		case ProgramAttributes['Location requirements']:
			return program.programFormat === ProgramFormat.IN_PERSON ||
				program.programFormat === ProgramFormat.HYBRID
				? renderCellWithTextLink(
						{
							introCopy: 'You need to live',
							linkCopy: 'in a specific location to apply.',
							onClick,
						},
						program
					)
				: 'None';
		case ProgramAttributes.Prerequisites:
			return program.contentItems.some(
				item => item.category === ProgramContentItemCategory.PREREQUISITE
			)
				? renderCellWithTextLink(
						{
							introCopy: 'There is',
							linkCopy: 'coursework',
							onClick,
							outroCopy: 'you have to complete before you apply.',
						},
						program
					)
				: 'None';
		case ProgramAttributes['GPA requirements']:
			return program.requirements.minCollegeGpa || program.requirements.minHsGpa
				? renderCellWithTextLink(
						{
							introCopy: "You'll have to meet minimum",
							linkCopy: 'grade requirements',
							onClick,
							outroCopy: 'to apply.',
						},
						program
					)
				: 'None';
		case ProgramAttributes['Skill requirements']:
			return program.contentItems.some(
				item => item.category === ProgramContentItemCategory.REQUIREMENTS_SKILLS
			)
				? renderCellWithTextLink(
						{
							introCopy: "You'll have to demonstrate",
							linkCopy: 'work-related skills',
							onClick,
							outroCopy: 'to apply.',
						},
						program
					)
				: 'None';
		case ProgramAttributes['Other requirements']:
			return program.contentItems.some(
				item => item.category === ProgramContentItemCategory.REQUIREMENTS_OTHER
			)
				? renderCellWithTextLink(
						{
							introCopy: "You'll have to fulfill",
							linkCopy: 'other requirements',
							onClick,
							outroCopy: 'to apply.',
						},
						program
					)
				: 'None';
		case ProgramAttributes['Work experience requirements']:
			return program.contentItems.some(
				item => item.category === ProgramContentItemCategory.REQUIREMENTS_WORK
			)
				? renderCellWithTextLink(
						{
							introCopy: "You'll have to demonstrate certain",
							linkCopy: 'work experiences',
							onClick,
							outroCopy: 'to apply.',
						},
						program
					)
				: 'None';
		case ProgramAttributes['Technology requirements']:
			return program.contentItems.some(
				item => item.category === ProgramContentItemCategory.REQUIREMENTS_TECHNOLOGY
			)
				? renderCellWithTextLink(
						{
							introCopy: "You'll need certain",
							linkCopy: 'computer or other tech supplies',
							onClick,
							outroCopy: 'to participate.',
						},
						program
					)
				: 'None';
		default:
			return 'Unavailable';
	}
};

const attributePopoverCopy = {
	[ProgramAttributes.Location]:
		'Programs can be delivered online via the internet, in-person at a specific location, or a hybrid that combines both.',
	[ProgramAttributes.Effort]:
		'This is an estimate of the time you may spend on this program each week, but it depends on factors like your academic skills and individual course demands.',
	[ProgramAttributes.Funding]:
		'The amount your employer will pay directly to your school each year.',
	[ProgramAttributes.Length]:
		'This is an estimate of the amount of time it may take to complete this program, but it depends on the number of credits you take each term, the number of terms you enroll in each year, and your continued academic progress.',
	[ProgramAttributes['Guild Coaching']]:
		'Free, personalized coaching support throughout your program.',
	[ProgramAttributes['Location requirements']]:
		'A specific location you may need to live in to apply.',
	[ProgramAttributes.Prerequisites]: 'Coursework you may need to complete before applying.',
	[ProgramAttributes['GPA requirements']]: 'Minimum grade level you may need to show to apply.',
	[ProgramAttributes['Skill requirements']]:
		'Work-related skills you may need to demonstrate to apply.',
	[ProgramAttributes['Other requirements']]: 'Other application requirements.',
	[ProgramAttributes['Work experience requirements']]:
		'Work experience you may need to show to apply.',
	[ProgramAttributes['Technology requirements']]:
		"Computer or other supplies you'll need to participate in this program.",
};

export function getAttributePopoverCopy(attribute: ProgramAttributes): string | null {
	return attributePopoverCopy[attribute] || null;
}
